import {Component, inject, OnInit} from '@angular/core';
import {NgIf} from "@angular/common";
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {AccessService} from "../../../services/access.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../../services/data.service";
import {BackupService} from "../../../services/backup.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {ViewMode} from "../../../interfaces";

@Component({
    selector: 'app-product-detail',
    imports: [NgIf, ReactiveFormsModule],
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-box me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Produkt anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Produkt bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Produkt
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <div class="row">
                        <div class="col-md-4">
                            <label>Produktgruppe</label>
                            <select class="form-select" formControlName="id_product_group" *ngIf="viewMode != ViewMode.VIEW">
                                <option [ngValue]="null">Bitte wählen</option>
                                @for (group of productGroups; track group.id) {
                                    <option [ngValue]="group.id">{{ group.name }}</option>
                                }
                            </select>
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ product?.product_group_name }}</p>
                        </div>
                        <div class="col-md-4">
                            <label>Nummer</label>
                            <input type="text" class="form-control" formControlName="number" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.number }}</p>
                        </div>
                        @if (viewMode != ViewMode.INSERT) {
                            <div class="col-md-4">
                                <label>Artikelnummer</label>
                                <p>{{ form.value.article_number }}</p>
                            </div>
                        }
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-6">
                            <label>Name</label>
                            <input type="text" class="form-control" formControlName="name" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.name }}</p>
                        </div>
                        <div class="col-md-6">
                            <label>EAN</label>
                            <input type="text" class="form-control" formControlName="EAN" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.EAN }}</p>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-4">
                            <label>Produkttyp</label>
                            <select class="form-select" formControlName="product_type" *ngIf="viewMode != ViewMode.VIEW">
                                <option [ngValue]="null">Bitte wählen</option>
                                <option value="Handelsprodukt">Handelsprodukt</option>
                                <option value="Eigenprodukt">Eigenprodukt</option>
                            </select>
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.product_type }}</p>
                        </div>
                        <div class="col-md-4">
                            <label>Status</label>
                            <select class="form-select" formControlName="status" *ngIf="viewMode != ViewMode.VIEW">
                                <option [ngValue]="null">Bitte wählen</option>
                                <option value="verpflichtend">verpflichtend</option>
                                <option value="optional">optional</option>
                            </select>
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.status }}</p>
                        </div>
                        <div class="col-md-4">
                            <label>Aktiv</label>
                            <select class="form-select" formControlName="active" *ngIf="viewMode != ViewMode.VIEW">
                                <option [ngValue]="null">Bitte wählen</option>
                                <option [ngValue]="1">Ja</option>
                                <option [ngValue]="0">Nein</option>
                            </select>
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.active == '1' ? 'Ja' : 'Nein' }}</p>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-6">
                            <label>Produktbild</label>
                            <input type="file" class="form-control" accept="image/*" (change)="onImageSelected($event)" *ngIf="viewMode != ViewMode.VIEW">
                            <div *ngIf="selectedImage || product?.image_hash" class="mt-2">
                                <img [src]="selectedImage || product?.file_path + product?.image_hash" alt="Product Image" style="max-width: 200px; max-height: 200px;">
                            </div>
                        </div>
                    </div>
                </form>

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary" (click)="cancel()">Zurück</button>
                        <button type="button" class="btn btn-danger float-end" (click)="delete()">Löschen</button>
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>

            </div>
        </div>
    `
})
export class ProductDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private backupService = inject(BackupService);
    private toastrService = inject(ToastrService);

    ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_product!: string;
    viewMode!: ViewMode;
    product: any;
    productGroups: any[] = [];
    selectedImage: string | null = null;

    ngOnInit(): void {
        this.id_product = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_product == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            id_product_group: [null, Validators.required],
            article_number: [null],
            number: [null, Validators.required],
            name: [null, Validators.required],
            product_type: [null, Validators.required],
            status: ['verpflichtend', Validators.required],
            active: [1, Validators.required],
            EAN: [null],
            image: [null],
        });

        this.loadProductGroups();

        if (this.id_product !== '0') {
            this.getProduct();
        }
    }

    loadProductGroups(): void {
        this.dataService.request('ProductGroup/listForSelection', {}).subscribe(response => {
            this.productGroups = response.productGroups;
        });
    }

    getProduct(): void {
        this.dataService.request('Product/get', {
            id: this.id_product
        }).subscribe(response => {
            this.product = response.product;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.product.id,
            id_product_group: this.product.id_product_group,
            article_number: this.product.article_number,
            number: this.product.number,
            name: this.product.name,
            product_type: this.product.product_type,
            status: this.product.status,
            active: this.product.active,
            EAN: this.product.EAN,
        });
    }

    save(): void {
        this.dataService.request('Product/save', {
            product: this.form.getRawValue(),
        }).subscribe(() => {
            this.router.navigate(['/product']);
            this.toastrService.success('Das Produkt wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('Product/update', {
            product: this.form.getRawValue(),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getProduct();
            this.toastrService.success('Das Produkt wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/product']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
        this.getProduct();
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Das Produkt wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Product/delete', {
                    id: this.id_product
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Das Produkt wurde gelöscht');
                });
            }
        });
    }

    onImageSelected(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                this.selectedImage = reader.result as string;
                this.form.patchValue({
                    image: reader.result as string
                });
            };
            reader.readAsDataURL(file);
        }
    }

}
